import * as client_bindings from './alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries: Map<string, T['node']>;

	constructor (entries:{[key:string]:T['init']},	protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, this.initialize(parent, entry_key, entries[entry_key])]));
	}

	protected abstract initialize(parent:P, key:string, obj:T['init']): T['node'];
	protected abstract finalize(obj:T['node'], detach?:boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator]():IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	entries(): IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	values(): IterableIterator<T['node']> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.values();
	}
	keys(): IterableIterator<string> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return Array.from(this.entries());
	}
	map<RT>(callback:(value:T['node']) => RT): Record<string, RT> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node']|undefined {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.get(key);
	}
	has(key:string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key:string, onExists:(($: T['node']) => RT)|Exclude<RT, Function>, onNotExists:(() => RT)|Exclude<RT, Function>): RT {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cclient_bindings
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cclient_bindings|undefined;
	public abstract get root():Cclient_bindings;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
type AlanGraphEdge<T extends AlanNode> = {
	ref: {
		entity: T
	}
};
abstract class AlanGraphVertex extends AlanDictionaryEntry {
	abstract _edges: { [key: string ]: Set<AlanGraphEdge<AlanGraphVertex>> }
}
export abstract class AlanTopology<T extends { node:AlanGraphVertex, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract _graphs: { [key: string ]: T['node'][] };
	topo_forEach(graph:G, walk_function: ($:T['node']) => void) {
		for (let entry of this.topo_entries(graph)) { walk_function(entry); }
	}
	topo_entries(graph:G):IterableIterator<T['node']> {
		return this._graphs[graph][Symbol.iterator]();
	}
	topo_toArray(graph:G):T['node'][] {
		return Array.from(this.topo_entries(graph));
	}
	topo_sort(g:G) {
		const $this = this;
		$this._graphs[g] = []
		const indegree:Map<T['node'], number> = new Map(Array.from($this.entries()).map(([_,v]) => [v as T['node'],0]));
		for (let [_,v] of $this.entries()) {
			v._edges[g].forEach(edge => indegree.set(edge.ref.entity as AlanGraphVertex, indegree.get(edge.ref.entity as AlanGraphVertex)! + 1));
		}
		let queue: T['node'][] = [];

		//find all vertices with indegree 0
		indegree.forEach((v,k) => { if (v === 0) queue.push(k); });
		let visited = 0;
		while (queue.length > 0) {
			++visited;
			const v = queue.pop()!;
			$this._graphs[g].push(v);
			v._edges[g].forEach(edge => {
				const u = indegree.get(edge.ref.entity as T['node'])!;
				if (u === 1) {
					queue.push(edge.ref.entity as T['node']);
				} else {
					indegree.set(edge.ref.entity as T['node'], u - 1)
				}
			});
		}

		// Check if there was a cycle
		if (visited !== this._entries.size) {
			throw new Error(`Cycle found in graph.`);
		}
	}
	totally_ordered(g:G) {
		if (this._graphs[g].length < 2) return;
		this._graphs[g].reduce((prev, curr) => {
			if (prev._edges[g].size === 0) { return curr; }
			let connected = false;
			prev._edges[g].forEach(e => { connected = (connected || e.ref.entity === curr) });
			if (!connected)
				throw new Error(`Totally ordered graph constraint violation.`);
			return curr;
		});
	}
}

/* alan objects */
type Vbinding_type = { name: 'user', definition: (typeof Cbinding_type.Puser)}|{ name: 'role', definition: (typeof Cbinding_type.Prole)}|{ name: 'query', definition: client_bindings.Cquery_binding_type}|{ name: 'report', definition: client_bindings.Creport_binding_type}|{ name: 'node', definition: client_bindings.Cnode_binding_type}|{ name: 'window', definition: (typeof Cbinding_type.Pwindow)}|{ name: 'view', definition: (typeof Cbinding_type.Pview)}|{ name: 'engine state', definition: (typeof Cbinding_type.Pengine_state)}
export class Cbinding_type extends AlanStruct {
	public static Puser:Cbinding_type = new class PrimitiveInstance extends Cbinding_type {
		constructor () {
			super({name: 'user', definition: undefined as unknown as Cbinding_type})
			this.variant.definition = this;
		}
	}
	public static Prole:Cbinding_type = new class PrimitiveInstance extends Cbinding_type {
		constructor () {
			super({name: 'role', definition: undefined as unknown as Cbinding_type})
			this.variant.definition = this;
		}
	}
	public static Pwindow:Cbinding_type = new class PrimitiveInstance extends Cbinding_type {
		constructor () {
			super({name: 'window', definition: undefined as unknown as Cbinding_type})
			this.variant.definition = this;
		}
	}
	public static Pview:Cbinding_type = new class PrimitiveInstance extends Cbinding_type {
		constructor () {
			super({name: 'view', definition: undefined as unknown as Cbinding_type})
			this.variant.definition = this;
		}
	}
	public static Pengine_state:Cbinding_type = new class PrimitiveInstance extends Cbinding_type {
		constructor () {
			super({name: 'engine state', definition: undefined as unknown as Cbinding_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vbinding_type) { super(); }
	public cast<K extends Vbinding_type['name']>(_variant:K):Extract<Vbinding_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vbinding_type['name']]:(($:Extract<Vbinding_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'binding type'`; }
	public is(other:Cbinding_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cengine_state__binding_type = client_bindings.Cbinding_type;
export import Crole__binding_type = client_bindings.Cbinding_type;
export import Cuser__binding_type = client_bindings.Cbinding_type;
export import Cview__binding_type = client_bindings.Cbinding_type;
export import Cwindow__binding_type = client_bindings.Cbinding_type;
type Vreport_binding_type = { name: 'number', definition: (typeof Creport_binding_type.Pnumber)}|{ name: 'text', definition: (typeof Creport_binding_type.Ptext)}|{ name: 'node', definition: (typeof Creport_binding_type.Pnode)}
export class Creport_binding_type extends AlanStruct {
	public static Pnumber:Creport_binding_type = new class PrimitiveInstance extends Creport_binding_type {
		constructor () {
			super({name: 'number', definition: undefined as unknown as Creport_binding_type})
			this.variant.definition = this;
		}
	}
	public static Ptext:Creport_binding_type = new class PrimitiveInstance extends Creport_binding_type {
		constructor () {
			super({name: 'text', definition: undefined as unknown as Creport_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pnode:Creport_binding_type = new class PrimitiveInstance extends Creport_binding_type {
		constructor () {
			super({name: 'node', definition: undefined as unknown as Creport_binding_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vreport_binding_type) { super(); }
	public definitions:{
		binding_type: Cbinding_type;
	} = {
		binding_type: new Cbinding_type({name:'report', definition: this})
	}
	public cast<K extends Vreport_binding_type['name']>(_variant:K):Extract<Vreport_binding_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vreport_binding_type['name']]:(($:Extract<Vreport_binding_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'report binding type'`; }
	public is(other:Creport_binding_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnode__report_binding_type = client_bindings.Creport_binding_type;
export import Cnumber__report_binding_type = client_bindings.Creport_binding_type;
export import Ctext__report_binding_type = client_bindings.Creport_binding_type;
type Vquery_binding_type = { name: 'stategroup column', definition: (typeof Cquery_binding_type.Pstategroup_column)}|{ name: 'stategroup entry', definition: (typeof Cquery_binding_type.Pstategroup_entry)}|{ name: 'file', definition: (typeof Cquery_binding_type.Pfile)}|{ name: 'text entry', definition: (typeof Cquery_binding_type.Ptext_entry)}|{ name: 'text column', definition: (typeof Cquery_binding_type.Ptext_column)}|{ name: 'number entry', definition: (typeof Cquery_binding_type.Pnumber_entry)}|{ name: 'number column', definition: (typeof Cquery_binding_type.Pnumber_column)}|{ name: 'entry', definition: (typeof Cquery_binding_type.Pentry)}
export class Cquery_binding_type extends AlanStruct {
	public static Pstategroup_column:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'stategroup column', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pstategroup_entry:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'stategroup entry', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pfile:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'file', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Ptext_entry:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'text entry', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Ptext_column:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'text column', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pnumber_entry:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'number entry', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pnumber_column:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'number column', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pentry:Cquery_binding_type = new class PrimitiveInstance extends Cquery_binding_type {
		constructor () {
			super({name: 'entry', definition: undefined as unknown as Cquery_binding_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vquery_binding_type) { super(); }
	public definitions:{
		binding_type: Cbinding_type;
	} = {
		binding_type: new Cbinding_type({name:'query', definition: this})
	}
	public cast<K extends Vquery_binding_type['name']>(_variant:K):Extract<Vquery_binding_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vquery_binding_type['name']]:(($:Extract<Vquery_binding_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'query binding type'`; }
	public is(other:Cquery_binding_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Centry__query_binding_type = client_bindings.Cquery_binding_type;
export import Cfile__query_binding_type = client_bindings.Cquery_binding_type;
export import Cnumber_column = client_bindings.Cquery_binding_type;
export import Cnumber_entry = client_bindings.Cquery_binding_type;
export import Cstategroup_column = client_bindings.Cquery_binding_type;
export import Cstategroup_entry = client_bindings.Cquery_binding_type;
export import Ctext_column = client_bindings.Cquery_binding_type;
export import Ctext_entry = client_bindings.Cquery_binding_type;
type Vnode_location = { name: 'collection', definition: client_bindings.Ccollection__type__properties}|{ name: 'state', definition: client_bindings.Cstates}|{ name: 'binding', definition: client_bindings.Cbindings__client_bindings}
export class Cnode_location extends AlanStruct {
	constructor(
		public readonly variant:Vnode_location, public input: {
			root: () => client_bindings.Cbindings__client_bindings
		}) { super(); }
	public readonly output:{
		key: () => client_bindings.Ckey;
		root: () => client_bindings.Cbindings__client_bindings;
	} = {
		key: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'binding': {
						const client_bindings__node_location_out___key___binding_nval = context.cast('binding');
						return resolve(context)
							.then(context => client_bindings__node_location_out___key___binding_nval)
							.then(context => context?.properties.type)
							.then(context => {
								const state_group = context;
								switch (state_group?.state.name) {
									case 'engine state': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case 'node': {
										const client_bindings__node_location_out___key___binding___node_nval = state_group.cast('node');
										return resolve(context)
											.then(context => client_bindings__node_location_out___key___binding___node_nval)
											.then(context => context?.properties.type)
											.then(context => {
												const state_group = context;
												switch (state_group?.state.name) {
													case 'collection': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'collection entry': {
														const client_bindings__node_location_out___key___binding___node___collection_entry_nval = state_group.cast('collection entry');
														return resolve(context)
															.then(context => client_bindings__node_location_out___key___binding___node___collection_entry_nval)
															.then(context => context?.definitions.entry_key)
															.then(context => context?.definitions.key).result;
													}
													case 'entity': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'file': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'group': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'join': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'joined entry': {
														const client_bindings__node_location_out___key___binding___node___joined_entry_nval = state_group.cast('joined entry');
														return resolve(context)
															.then(context => client_bindings__node_location_out___key___binding___node___joined_entry_nval)
															.then(context => context?.definitions.entry_key)
															.then(context => context?.definitions.key).result;
													}
													case 'node': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'number': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'operation': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'ordered graph': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'query': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'sort property': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'state': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'state group': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'text': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'where rule': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
												};}).result;
									}
									case 'query': {
										const client_bindings__node_location_out___key___binding___query_nval = state_group.cast('query');
										return resolve(context)
											.then(context => client_bindings__node_location_out___key___binding___query_nval)
											.then(context => context?.properties.type)
											.then(context => {
												const state_group = context;
												switch (state_group?.state.name) {
													case 'entry': {
														const client_bindings__node_location_out___key___binding___query___entry_nval = state_group.cast('entry');
														return resolve(context)
															.then(context => client_bindings__node_location_out___key___binding___query___entry_nval)
															.then(context => context?.definitions.entry_key)
															.then(context => context?.definitions.key).result;
													}
													case 'file': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'number': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'stategroup': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case 'text': {
														return resolve(context)
															.then(() => this)
															.then(() => client_bindings.Ckey.Pnone).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
												};}).result;
									}
									case 'report': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case 'role': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case 'user': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case 'view': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case 'window': {
										return resolve(context)
											.then(() => this)
											.then(() => client_bindings.Ckey.Pnone).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
								};}).result;
					}
					case 'collection': {
						const client_bindings__node_location_out___key___collection_nval = context.cast('collection');
						return resolve(context)
							.then(context => client_bindings__node_location_out___key___collection_nval)
							.then(context => context?.definitions.entry_key)
							.then(context => context?.definitions.key).result;
					}
					case 'state': {
						return resolve(context)
							.then(() => this)
							.then(() => client_bindings.Ckey.Pnone).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		root: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.root()).result!)
	};
	public cast<K extends Vnode_location['name']>(_variant:K):Extract<Vnode_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_location['name']]:(($:Extract<Vnode_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node location'`; }
	public is(other:Cnode_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vnode_binding_type = { name: 'entity', definition: (typeof Cnode_binding_type.Pentity)}|{ name: 'sort property', definition: (typeof Cnode_binding_type.Psort_property)}|{ name: 'where rule', definition: (typeof Cnode_binding_type.Pwhere_rule)}|{ name: 'state', definition: (typeof Cnode_binding_type.Pstate)}|{ name: 'state group', definition: (typeof Cnode_binding_type.Pstate_group)}|{ name: 'group', definition: (typeof Cnode_binding_type.Pgroup)}|{ name: 'operation', definition: (typeof Cnode_binding_type.Poperation)}|{ name: 'query', definition: (typeof Cnode_binding_type.Pquery)}|{ name: 'ordered graph', definition: (typeof Cnode_binding_type.Pordered_graph)}|{ name: 'join', definition: (typeof Cnode_binding_type.Pjoin)}|{ name: 'joined entry', definition: (typeof Cnode_binding_type.Pjoined_entry)}|{ name: 'collection entry', definition: (typeof Cnode_binding_type.Pcollection_entry)}|{ name: 'collection', definition: (typeof Cnode_binding_type.Pcollection)}|{ name: 'file', definition: (typeof Cnode_binding_type.Pfile)}|{ name: 'text', definition: (typeof Cnode_binding_type.Ptext)}|{ name: 'number', definition: (typeof Cnode_binding_type.Pnumber)}|{ name: 'node', definition: (typeof Cnode_binding_type.Pnode)}
export class Cnode_binding_type extends AlanStruct {
	public static Pentity:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'entity', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Psort_property:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'sort property', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pwhere_rule:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'where rule', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pstate:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'state', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pstate_group:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'state group', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pgroup:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'group', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Poperation:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'operation', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pquery:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'query', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pordered_graph:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'ordered graph', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pjoin:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'join', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pjoined_entry:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'joined entry', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pcollection_entry:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'collection entry', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pcollection:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'collection', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pfile:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'file', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Ptext:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'text', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pnumber:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'number', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	public static Pnode:Cnode_binding_type = new class PrimitiveInstance extends Cnode_binding_type {
		constructor () {
			super({name: 'node', definition: undefined as unknown as Cnode_binding_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnode_binding_type) { super(); }
	public definitions:{
		binding_type: Cbinding_type;
	} = {
		binding_type: new Cbinding_type({name:'node', definition: this})
	}
	public cast<K extends Vnode_binding_type['name']>(_variant:K):Extract<Vnode_binding_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_binding_type['name']]:(($:Extract<Vnode_binding_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node binding type'`; }
	public is(other:Cnode_binding_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Ccollection__node_binding_type = client_bindings.Cnode_binding_type;
export import Ccollection_entry__node_binding_type = client_bindings.Cnode_binding_type;
export import Centity__node_binding_type = client_bindings.Cnode_binding_type;
export import Cfile__node_binding_type = client_bindings.Cnode_binding_type;
export import Cgroup__node_binding_type = client_bindings.Cnode_binding_type;
export import Cjoin__node_binding_type = client_bindings.Cnode_binding_type;
export import Cjoined_entry__node_binding_type = client_bindings.Cnode_binding_type;
export import Cnode__node_binding_type = client_bindings.Cnode_binding_type;
export import Cnumber__node_binding_type = client_bindings.Cnode_binding_type;
export import Coperation__node_binding_type = client_bindings.Cnode_binding_type;
export import Cordered_graph__node_binding_type = client_bindings.Cnode_binding_type;
export import Cquery__node_binding_type = client_bindings.Cnode_binding_type;
export import Csort_property__node_binding_type = client_bindings.Cnode_binding_type;
export import Cstate__node_binding_type = client_bindings.Cnode_binding_type;
export import Cstate_group__node_binding_type = client_bindings.Cnode_binding_type;
export import Ctext__node_binding_type = client_bindings.Cnode_binding_type;
export import Cwhere_rule__node_binding_type = client_bindings.Cnode_binding_type;
type Vkey = { name: 'entry', definition: client_bindings.Centry_key}|{ name: 'none', definition: (typeof Ckey.Pnone)}
export class Ckey extends AlanStruct {
	public static Pnone:Ckey = new class PrimitiveInstance extends Ckey {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Ckey})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vkey) { super(); }
	public cast<K extends Vkey['name']>(_variant:K):Extract<Vkey, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vkey['name']]:(($:Extract<Vkey, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'key'`; }
	public is(other:Ckey):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone = client_bindings.Ckey;
type Ventry_key = { name: 'query entry', definition: client_bindings.Centry__type__query}|{ name: 'joined entry', definition: client_bindings.Cjoined_entry__type}|{ name: 'collection entry', definition: client_bindings.Ccollection_entry__type}|{ name: 'collection', definition: client_bindings.Ccollection__type__properties}
export class Centry_key extends AlanStruct {
	constructor(
		public readonly variant:Ventry_key) { super(); }
	public definitions:{
		key: Ckey;
	} = {
		key: new Ckey({name:'entry', definition: this})
	}
	public cast<K extends Ventry_key['name']>(_variant:K):Extract<Ventry_key, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventry_key['name']]:(($:Extract<Ventry_key, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entry key'`; }
	public is(other:Centry_key):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vbinding_context = { name: 'node', definition: client_bindings.Cnode__client_bindings}|{ name: 'undefined', definition: (typeof Cbinding_context.Pundefined)}
export class Cbinding_context extends AlanStruct {
	public static Pundefined:Cbinding_context = new class PrimitiveInstance extends Cbinding_context {
		constructor () {
			super({name: 'undefined', definition: undefined as unknown as Cbinding_context})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vbinding_context) { super(); }
	public cast<K extends Vbinding_context['name']>(_variant:K):Extract<Vbinding_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vbinding_context['name']]:(($:Extract<Vbinding_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'binding context'`; }
	public is(other:Cbinding_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cundefined = client_bindings.Cbinding_context;
export type Tnode__client_bindings = {
	'bindings':Record<string, Tbindings__node>;
	'has widget':'no'|['no', {}]|'yes'|['yes', {}];
	'instructions':Record<string, Tinstructions>;
	'properties':Record<string, Tproperties>;
	'sortable':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cnode__client_bindings extends AlanNode {
	public definitions:{
		binding_context: Cbinding_context;
	} = {
		binding_context: new Cbinding_context({name:'node', definition: this})
	}
	public readonly properties:{
		readonly bindings:Cnode__client_bindings.Dbindings,
		readonly has_widget:Cnode__client_bindings.Dhas_widget,
		readonly instructions:Cnode__client_bindings.Dinstructions,
		readonly properties:Cnode__client_bindings.Dproperties,
		readonly sortable:Cnode__client_bindings.Dsortable
	};
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
		location: () => client_bindings.Cnode_location;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.binding_type()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!)
	};
	constructor(init:Tnode__client_bindings, public location:AlanNode, public input: {
		binding_type: () => client_bindings.Cbinding_type,
		location: () => client_bindings.Cnode_location
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			bindings: new Cnode__client_bindings.Dbindings(init['bindings'], $this),
			has_widget: new Cnode__client_bindings.Dhas_widget(init['has widget'], $this),
			instructions: new Cnode__client_bindings.Dinstructions(init['instructions'], $this),
			properties: new Cnode__client_bindings.Dproperties(init['properties'], $this),
			sortable: new Cnode__client_bindings.Dsortable(init['sortable'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbindings__node = {
	'binding':string;
	'switchable in configuration context path':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cbindings__node extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly binding:Cbindings__node.Dbinding,
		readonly switchable_in_configuration_context_path:Cbindings__node.Dswitchable_in_configuration_context_path
	};
	constructor(key:string, init:Tbindings__node, public parent:Cnode__client_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			binding: new Cbindings__node.Dbinding(init['binding'], $this),
			switchable_in_configuration_context_path: new Cbindings__node.Dswitchable_in_configuration_context_path(init['switchable in configuration context path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'bindings'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__switchable_in_configuration_context_path = {
};
export class Cno__switchable_in_configuration_context_path extends AlanNode {
	constructor(init:Tno__switchable_in_configuration_context_path, public parent:Cbindings__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switchable in configuration context path'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__switchable_in_configuration_context_path = {
};
export class Cyes__switchable_in_configuration_context_path extends AlanNode {
	constructor(init:Tyes__switchable_in_configuration_context_path, public parent:Cbindings__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switchable in configuration context path'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_widget = {
};
export class Cno__has_widget extends AlanNode {
	constructor(init:Tno__has_widget, public parent:Cnode__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has widget'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_widget = {
};
export class Cyes__has_widget extends AlanNode {
	constructor(init:Tyes__has_widget, public parent:Cnode__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has widget'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstructions = {
	'has arguments':'no'|['no', {}]|['yes', Tyes__has_arguments];
};
export class Cinstructions extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_arguments:Cinstructions.Dhas_arguments
	};
	constructor(key:string, init:Tinstructions, public parent:Cnode__client_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_arguments: new Cinstructions.Dhas_arguments(init['has arguments'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instructions'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_arguments = {
};
export class Cno__has_arguments extends AlanNode {
	constructor(init:Tno__has_arguments, public parent:Cinstructions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_arguments = {
	'first argument':Tinstruction_argument;
};
export class Cyes__has_arguments extends AlanNode {
	public readonly properties:{
		readonly first_argument:Cinstruction_argument
	};
	constructor(init:Tyes__has_arguments, public parent:Cinstructions) {
		super();
		const $this = this;
		this.properties = {
			first_argument: new Cyes__has_arguments.Dfirst_argument(init['first argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperties = {
	'type':['collection', Tcollection__type__properties]|'number'|['number', {}]|['reference', Treference]|['state group', Tstate_group__type__properties]|'text'|['text', {}];
};
export class Cproperties extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly type:Cproperties.Dtype
	};
	constructor(key:string, init:Tproperties, public parent:Cnode__client_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			type: new Cproperties.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties = {
	'node':Tnode__client_bindings;
};
export class Ccollection__type__properties extends AlanNode {
	public definitions:{
		entry_key: Centry_key;
		node_location: Cnode_location;
	} = {
		entry_key: new Centry_key({name:'collection', definition: this}),
		node_location: new Cnode_location({name:'collection', definition: this}, {
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly node:Cnode__client_bindings
	};
	constructor(init:Tcollection__type__properties, public parent:Cproperties) {
		super();
		const $this = this;
		this.properties = {
			node: new Ccollection__type__properties.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties = {
};
export class Cnumber__type__properties extends AlanNode {
	constructor(init:Tnumber__type__properties, public parent:Cproperties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference = {
	'can navigate binding':'no'|['no', {}]|['yes', Tyes__can_navigate_binding];
};
export class Creference extends AlanNode {
	public readonly properties:{
		readonly can_navigate_binding:Creference.Dcan_navigate_binding
	};
	constructor(init:Treference, public parent:Cproperties) {
		super();
		const $this = this;
		this.properties = {
			can_navigate_binding: new Creference.Dcan_navigate_binding(init['can navigate binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_navigate_binding = {
};
export class Cno__can_navigate_binding extends AlanNode {
	constructor(init:Tno__can_navigate_binding, public parent:Creference) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can navigate binding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_navigate_binding = {
	'binding type path':Tbinding_type_path;
	'context':['root', Troot]|'this'|['this', {}];
};
export class Cyes__can_navigate_binding extends AlanNode {
	public readonly properties:{
		readonly binding_type_path:Cbinding_type_path,
		readonly context:Cyes__can_navigate_binding.Dcontext
	};
	constructor(init:Tyes__can_navigate_binding, public parent:Creference) {
		super();
		const $this = this;
		this.properties = {
			binding_type_path: new Cyes__can_navigate_binding.Dbinding_type_path(init['binding type path'], $this),
			context: new Cyes__can_navigate_binding.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can navigate binding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot = {
	'binding':string;
};
export class Croot extends AlanNode {
	public readonly properties:{
		readonly binding:Croot.Dbinding
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding).result!)
	}
	constructor(init:Troot, public parent:Cyes__can_navigate_binding) {
		super();
		const $this = this;
		this.properties = {
			binding: new Croot.Dbinding(init['binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tthis = {
};
export class Cthis extends AlanNode {
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location())
			.then(context => context?.component_root.output.root())
			.then(context => context?.properties.binding).result!)
	}
	constructor(init:Tthis, public parent:Cyes__can_navigate_binding) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'this'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties = {
	'states':Record<string, Tstates>;
};
export class Cstate_group__type__properties extends AlanNode {
	public readonly properties:{
		readonly states:Cstate_group__type__properties.Dstates
	};
	constructor(init:Tstate_group__type__properties, public parent:Cproperties) {
		super();
		const $this = this;
		this.properties = {
			states: new Cstate_group__type__properties.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates = {
	'node':Tnode__client_bindings;
};
export class Cstates extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		node_location: Cnode_location;
	} = {
		node_location: new Cnode_location({name:'state', definition: this}, {
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly node:Cnode__client_bindings
	};
	constructor(key:string, init:Tstates, public parent:Cstate_group__type__properties) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			node: new Cstates.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key}']`; }
	public get entity() { return this; }
}
export type Ttext__type__properties = {
};
export class Ctext__type__properties extends AlanNode {
	constructor(init:Ttext__type__properties, public parent:Cproperties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__sortable = {
};
export class Cno__sortable extends AlanNode {
	constructor(init:Tno__sortable, public parent:Cnode__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sortable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__sortable = {
};
export class Cyes__sortable extends AlanNode {
	constructor(init:Tyes__sortable, public parent:Cnode__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sortable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstruction_argument = {
	'next argument':['exists', Texists]|'no'|['no', {}];
	'type':'file'|['file', {}]|'number'|['number', {}]|'text'|['text', {}]|'view'|['view', {}];
};
export class Cinstruction_argument extends AlanNode {
	public readonly properties:{
		readonly next_argument:Cinstruction_argument.Dnext_argument,
		readonly type:Cinstruction_argument.Dtype
	};
	constructor(init:Tinstruction_argument, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			next_argument: new Cinstruction_argument.Dnext_argument(init['next argument'], $this),
			type: new Cinstruction_argument.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texists = {
	'argument':Tinstruction_argument;
};
export class Cexists extends AlanNode {
	public readonly properties:{
		readonly argument:Cinstruction_argument
	};
	constructor(init:Texists, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cexists.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__next_argument = {
};
export class Cno__next_argument extends AlanNode {
	constructor(init:Tno__next_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__instruction_argument = {
};
export class Cfile__type__instruction_argument extends AlanNode {
	constructor(init:Tfile__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__instruction_argument = {
};
export class Cnumber__type__instruction_argument extends AlanNode {
	constructor(init:Tnumber__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__instruction_argument = {
};
export class Ctext__type__instruction_argument extends AlanNode {
	constructor(init:Ttext__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type__instruction_argument = {
};
export class Cview__type__instruction_argument extends AlanNode {
	constructor(init:Tview__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding_type_path = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next];
	'type':['binding', Tbinding]|['property', Tproperty];
};
export class Cbinding_type_path extends AlanNode {
	public readonly properties:{
		readonly has_next:Cbinding_type_path.Dhas_next,
		readonly type:Cbinding_type_path.Dtype
	};
	public readonly output:{
		result: () => client_bindings.Cnode__client_bindings;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_next?.state.node.output.result()).result!)
	};
	constructor(init:Tbinding_type_path, public location:AlanNode, public input: {
		context: () => client_bindings.Cnode__client_bindings
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_next: new Cbinding_type_path.Dhas_next(init['has next'], $this),
			type: new Cbinding_type_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next = {
};
export class Cno__has_next extends AlanNode {
	public readonly output:{
		result: () => client_bindings.Cnode__client_bindings;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!)
	}
	constructor(init:Tno__has_next, public parent:Cbinding_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next = {
	'tail':Tbinding_type_path;
};
export class Cyes__has_next extends AlanNode {
	public readonly properties:{
		readonly tail:Cbinding_type_path
	};
	public readonly output:{
		result: () => client_bindings.Cnode__client_bindings;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result()).result!)
	}
	constructor(init:Tyes__has_next, public parent:Cbinding_type_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_next.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding = {
	'binding':string;
};
export class Cbinding extends AlanNode {
	public readonly properties:{
		readonly binding:Cbinding.Dbinding
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding).result!)
	}
	constructor(init:Tbinding, public parent:Cbinding_type_path) {
		super();
		const $this = this;
		this.properties = {
			binding: new Cbinding.Dbinding(init['binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty = {
	'type':['collection', Tcollection__type__property]|['state', Tstate__type__property];
};
export class Cproperty extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty.Dtype
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.node()).result!)
	}
	constructor(init:Tproperty, public parent:Cbinding_type_path) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property = {
	'collection':string;
};
export class Ccollection__type__property extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__type__property.Dcollection
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__type__property.Dcollection(init['collection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__property = {
	'state':string;
	'state group':string;
};
export class Cstate__type__property extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__property.Dstate,
		readonly state_group:Cstate__type__property.Dstate_group
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__property.Dstate(init['state'], $this),
			state_group: new Cstate__type__property.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}

export type Tclient_bindings = {
	'bindings':Record<string, Tbindings__client_bindings>;
	'formatters':Record<string, Tformatters>;
	'initial view binding':string;
	'transformers':Record<string, Ttransformers>;
};
export class Cclient_bindings extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly bindings:Cclient_bindings.Dbindings,
		readonly formatters:Cclient_bindings.Dformatters,
		readonly initial_view_binding:Cclient_bindings.Dinitial_view_binding,
		readonly transformers:Cclient_bindings.Dtransformers
	};
	constructor(init:Tclient_bindings, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			bindings: new Cclient_bindings.Dbindings(init['bindings'], $this),
			formatters: new Cclient_bindings.Dformatters(init['formatters'], $this),
			initial_view_binding: new Cclient_bindings.Dinitial_view_binding(init['initial view binding'], $this),
			transformers: new Cclient_bindings.Dtransformers(init['transformers'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tbindings__client_bindings = {
	'binding':Tnode__client_bindings;
	'constrained on containing binding':'no'|['no', {}]|'yes'|['yes', {}];
	'type':'engine state'|['engine state', {}]|['node', Tnode__type__bindings]|['query', Tquery__type__bindings]|['report', Treport]|'role'|['role', {}]|'user'|['user', {}]|'view'|['view', {}]|'window'|['window', {}];
};
export class Cbindings__client_bindings extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		node_location: Cnode_location;
	} = {
		node_location: new Cnode_location({name:'binding', definition: this}, {
			root: cache((detach:boolean) => resolve(this)
				.then(() => this).result!)
		})
	}
	public readonly properties:{
		readonly binding:Cnode__client_bindings,
		readonly constrained_on_containing_binding:Cbindings__client_bindings.Dconstrained_on_containing_binding,
		readonly type:Cbindings__client_bindings.Dtype
	};
	constructor(key:string, init:Tbindings__client_bindings, public parent:Cclient_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			binding: new Cbindings__client_bindings.Dbinding(init['binding'], $this),
			constrained_on_containing_binding: new Cbindings__client_bindings.Dconstrained_on_containing_binding(init['constrained on containing binding'], $this),
			type: new Cbindings__client_bindings.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'bindings'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__constrained_on_containing_binding = {
};
export class Cno__constrained_on_containing_binding extends AlanNode {
	constructor(init:Tno__constrained_on_containing_binding, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__constrained_on_containing_binding = {
};
export class Cyes__constrained_on_containing_binding extends AlanNode {
	constructor(init:Tyes__constrained_on_containing_binding, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine_state__type = {
};
export class Cengine_state__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_type.Pengine_state).result!)
	}
	constructor(init:Tengine_state__type, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'engine state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__bindings = {
	'type':'collection'|['collection', {}]|'collection entry'|['collection entry', {}]|'entity'|['entity', {}]|'file'|['file', {}]|'group'|['group', {}]|'join'|['join', {}]|'joined entry'|['joined entry', {}]|'node'|['node', {}]|'number'|['number', {}]|'operation'|['operation', {}]|'ordered graph'|['ordered graph', {}]|'query'|['query', {}]|'sort property'|['sort property', {}]|'state'|['state', {}]|'state group'|['state group', {}]|'text'|['text', {}]|'where rule'|['where rule', {}];
};
export class Cnode__type__bindings extends AlanNode {
	public readonly properties:{
		readonly type:Cnode__type__bindings.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type())
			.then(context => context?.definitions.binding_type).result!)
	}
	constructor(init:Tnode__type__bindings, public parent:Cbindings__client_bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode__type__bindings.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__node = {
};
export class Ccollection__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pcollection).result!)
	}
	constructor(init:Tcollection__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type = {
};
export class Ccollection_entry__type extends AlanNode {
	public definitions:{
		entry_key: Centry_key;
	} = {
		entry_key: new Centry_key({name:'collection entry', definition: this})
	}
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pcollection_entry).result!)
	}
	constructor(init:Tcollection_entry__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentity__type = {
};
export class Centity__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pentity).result!)
	}
	constructor(init:Tentity__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entity'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__node = {
};
export class Cfile__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pfile).result!)
	}
	constructor(init:Tfile__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type = {
};
export class Cgroup__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pgroup).result!)
	}
	constructor(init:Tgroup__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tjoin__type = {
};
export class Cjoin__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pjoin).result!)
	}
	constructor(init:Tjoin__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'join'`; }
	public get entity() { return this.parent.entity; }
}
export type Tjoined_entry__type = {
};
export class Cjoined_entry__type extends AlanNode {
	public definitions:{
		entry_key: Centry_key;
	} = {
		entry_key: new Centry_key({name:'joined entry', definition: this})
	}
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pjoined_entry).result!)
	}
	constructor(init:Tjoined_entry__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'joined entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__node = {
};
export class Cnode__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pnode).result!)
	}
	constructor(init:Tnode__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__node = {
};
export class Cnumber__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pnumber).result!)
	}
	constructor(init:Tnumber__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Toperation__type = {
};
export class Coperation__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Poperation).result!)
	}
	constructor(init:Toperation__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordered_graph__type = {
};
export class Cordered_graph__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pordered_graph).result!)
	}
	constructor(init:Tordered_graph__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'ordered graph'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__type__node = {
};
export class Cquery__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pquery).result!)
	}
	constructor(init:Tquery__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsort_property__type = {
};
export class Csort_property__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Psort_property).result!)
	}
	constructor(init:Tsort_property__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sort property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__node = {
};
export class Cstate__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pstate).result!)
	}
	constructor(init:Tstate__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__node = {
};
export class Cstate_group__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pstate_group).result!)
	}
	constructor(init:Tstate_group__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__node = {
};
export class Ctext__type__node extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Ptext).result!)
	}
	constructor(init:Ttext__type__node, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Twhere_rule__type = {
};
export class Cwhere_rule__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cnode_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cnode_binding_type.Pwhere_rule).result!)
	}
	constructor(init:Twhere_rule__type, public parent:Cnode__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'where rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__type__bindings = {
	'type':'entry'|['entry', {}]|'file'|['file', {}]|['number', Tnumber__type__query]|['stategroup', Tstategroup]|['text', Ttext__type__query];
};
export class Cquery__type__bindings extends AlanNode {
	public readonly properties:{
		readonly type:Cquery__type__bindings.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type())
			.then(context => context?.definitions.binding_type).result!)
	}
	constructor(init:Tquery__type__bindings, public parent:Cbindings__client_bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Cquery__type__bindings.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__type__query = {
};
export class Centry__type__query extends AlanNode {
	public definitions:{
		entry_key: Centry_key;
	} = {
		entry_key: new Centry_key({name:'query entry', definition: this})
	}
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pentry).result!)
	}
	constructor(init:Tentry__type__query, public parent:Cquery__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__query = {
};
export class Cfile__type__query extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pfile).result!)
	}
	constructor(init:Tfile__type__query, public parent:Cquery__type__bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__query = {
	'type':'column'|['column', {}]|'entry'|['entry', {}];
};
export class Cnumber__type__query extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber__type__query.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type()).result!)
	}
	constructor(init:Tnumber__type__query, public parent:Cquery__type__bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber__type__query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcolumn__type__number = {
};
export class Ccolumn__type__number extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pnumber_column).result!)
	}
	constructor(init:Tcolumn__type__number, public parent:Cnumber__type__query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'column'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__type__number = {
};
export class Centry__type__number extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pnumber_entry).result!)
	}
	constructor(init:Tentry__type__number, public parent:Cnumber__type__query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstategroup = {
	'type':'column'|['column', {}]|'entry'|['entry', {}];
};
export class Cstategroup extends AlanNode {
	public readonly properties:{
		readonly type:Cstategroup.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type()).result!)
	}
	constructor(init:Tstategroup, public parent:Cquery__type__bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Cstategroup.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'stategroup'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcolumn__type__stategroup = {
};
export class Ccolumn__type__stategroup extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pstategroup_column).result!)
	}
	constructor(init:Tcolumn__type__stategroup, public parent:Cstategroup) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'column'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__type__stategroup = {
};
export class Centry__type__stategroup extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Pstategroup_entry).result!)
	}
	constructor(init:Tentry__type__stategroup, public parent:Cstategroup) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__query = {
	'type':'column'|['column', {}]|'entry'|['entry', {}];
};
export class Ctext__type__query extends AlanNode {
	public readonly properties:{
		readonly type:Ctext__type__query.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type()).result!)
	}
	constructor(init:Ttext__type__query, public parent:Cquery__type__bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext__type__query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcolumn__type__text = {
};
export class Ccolumn__type__text extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Ptext_column).result!)
	}
	constructor(init:Tcolumn__type__text, public parent:Ctext__type__query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'column'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__type__text = {
};
export class Centry__type__text extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cquery_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cquery_binding_type.Ptext_entry).result!)
	}
	constructor(init:Tentry__type__text, public parent:Ctext__type__query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Treport = {
	'type':'node'|['node', {}]|'number'|['number', {}]|'text'|['text', {}];
};
export class Creport extends AlanNode {
	public readonly properties:{
		readonly type:Creport.Dtype
	};
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.binding_type())
			.then(context => context?.definitions.binding_type).result!)
	}
	constructor(init:Treport, public parent:Cbindings__client_bindings) {
		super();
		const $this = this;
		this.properties = {
			type: new Creport.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'report'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__report = {
};
export class Cnode__type__report extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Creport_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Creport_binding_type.Pnode).result!)
	}
	constructor(init:Tnode__type__report, public parent:Creport) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__report = {
};
export class Cnumber__type__report extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Creport_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Creport_binding_type.Pnumber).result!)
	}
	constructor(init:Tnumber__type__report, public parent:Creport) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__report = {
};
export class Ctext__type__report extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Creport_binding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Creport_binding_type.Ptext).result!)
	}
	constructor(init:Ttext__type__report, public parent:Creport) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Trole__type = {
};
export class Crole__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_type.Prole).result!)
	}
	constructor(init:Trole__type, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'role'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__type = {
};
export class Cuser__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_type.Puser).result!)
	}
	constructor(init:Tuser__type, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type__bindings = {
};
export class Cview__type__bindings extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_type.Pview).result!)
	}
	constructor(init:Tview__type__bindings, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow__type = {
};
export class Cwindow__type extends AlanNode {
	public readonly output:{
		binding_type: () => client_bindings.Cbinding_type;
	} = {
		binding_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_type.Pwindow).result!)
	}
	constructor(init:Twindow__type, public parent:Cbindings__client_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
export type Tformatters = {
	'from':'number'|['number', {}]|'text'|['text', {}];
};
export class Cformatters extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly from:Cformatters.Dfrom
	};
	constructor(key:string, init:Tformatters, public parent:Cclient_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			from: new Cformatters.Dfrom(init['from'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'formatters'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnumber__from__formatters = {
};
export class Cnumber__from__formatters extends AlanNode {
	constructor(init:Tnumber__from__formatters, public parent:Cformatters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__from__formatters = {
};
export class Ctext__from__formatters extends AlanNode {
	constructor(init:Ttext__from__formatters, public parent:Cformatters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttransformers = {
	'from':'number'|['number', {}]|'text'|['text', {}];
};
export class Ctransformers extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly from:Ctransformers.Dfrom
	};
	constructor(key:string, init:Ttransformers, public parent:Cclient_bindings) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			from: new Ctransformers.Dfrom(init['from'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'transformers'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnumber__from__transformers = {
};
export class Cnumber__from__transformers extends AlanNode {
	constructor(init:Tnumber__from__transformers, public parent:Ctransformers) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__from__transformers = {
};
export class Ctext__from__transformers extends AlanNode {
	constructor(init:Ttext__from__transformers, public parent:Ctransformers) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'text'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cbinding_type_path {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next, init:Tno__has_next}|
		{ name: 'yes', node:Cyes__has_next, init:Tyes__has_next}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next, parent:Cbinding_type_path) => new Cno__has_next(init, parent);
				case 'yes': return (init:Tyes__has_next, parent:Cbinding_type_path) => new Cyes__has_next(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next;
				case 'yes': return finalize_yes__has_next;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding_type_path['has next'], parent:Cbinding_type_path) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding, init:Tbinding}|
		{ name: 'property', node:Cproperty, init:Tproperty}> {
		protected initializer(state: 'binding'|'property') {
			switch (state) {
				case 'binding': return (init:Tbinding, parent:Cbinding_type_path) => new Cbinding(init, parent);
				case 'property': return (init:Tproperty, parent:Cbinding_type_path) => new Cproperty(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'property') {
			switch (state) {
				case 'binding': return finalize_binding;
				case 'property': return finalize_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding_type_path['type'], parent:Cbinding_type_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_next {
	export class Dtail extends Cbinding_type_path {
		constructor(data:Tyes__has_next['tail'], parent:Cyes__has_next) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.node()).result!)
			}, 'tail')
		}
	}
}
export namespace Cbinding {
	export class Dbinding extends Reference<client_bindings.Cbindings__node,string> {

		constructor(data:string, $this:Cbinding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
}
export namespace Cproperty {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property, init:Tcollection__type__property}|
		{ name: 'state', node:Cstate__type__property, init:Tstate__type__property}> {
		protected initializer(state: 'collection'|'state') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property, parent:Cproperty) => new Ccollection__type__property(init, parent);
				case 'state': return (init:Tstate__type__property, parent:Cproperty) => new Cstate__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'state') {
			switch (state) {
				case 'collection': return finalize_collection__type__property;
				case 'state': return finalize_state__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty['type'], parent:Cproperty) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property {
	export class Dcollection extends Reference<client_bindings.Ccollection__type__properties,string> {

		constructor(data:string, $this:Ccollection__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_bindings.Ccollection__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cstate__type__property {
	export class Dstate extends Reference<client_bindings.Cstates,string> {

		constructor(data:string, $this:Cstate__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<client_bindings.Cstate_group__type__properties,string> {

		constructor(data:string, $this:Cstate__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as client_bindings.Cstate_group__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cinstruction_argument {
	export class Dnext_argument extends StateGroup<
		{ name: 'exists', node:Cexists, init:Texists}|
		{ name: 'no', node:Cno__next_argument, init:Tno__next_argument}> {
		protected initializer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return (init:Texists, parent:Cinstruction_argument) => new Cexists(init, parent);
				case 'no': return (init:Tno__next_argument, parent:Cinstruction_argument) => new Cno__next_argument(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return finalize_exists;
				case 'no': return finalize_no__next_argument;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_argument['next argument'], parent:Cinstruction_argument) {
			super(data, parent, 'next argument');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'next argument'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile__type__instruction_argument, init:Tfile__type__instruction_argument}|
		{ name: 'number', node:Cnumber__type__instruction_argument, init:Tnumber__type__instruction_argument}|
		{ name: 'text', node:Ctext__type__instruction_argument, init:Ttext__type__instruction_argument}|
		{ name: 'view', node:Cview__type__instruction_argument, init:Tview__type__instruction_argument}> {
		protected initializer(state: 'file'|'number'|'text'|'view') {
			switch (state) {
				case 'file': return (init:Tfile__type__instruction_argument, parent:Cinstruction_argument) => new Cfile__type__instruction_argument(init, parent);
				case 'number': return (init:Tnumber__type__instruction_argument, parent:Cinstruction_argument) => new Cnumber__type__instruction_argument(init, parent);
				case 'text': return (init:Ttext__type__instruction_argument, parent:Cinstruction_argument) => new Ctext__type__instruction_argument(init, parent);
				case 'view': return (init:Tview__type__instruction_argument, parent:Cinstruction_argument) => new Cview__type__instruction_argument(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'file'|'number'|'text'|'view') {
			switch (state) {
				case 'file': return finalize_file__type__instruction_argument;
				case 'number': return finalize_number__type__instruction_argument;
				case 'text': return finalize_text__type__instruction_argument;
				case 'view': return finalize_view__type__instruction_argument;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_argument['type'], parent:Cinstruction_argument) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cexists {
	export class Dargument extends Cinstruction_argument {
		constructor(data:Texists['argument'], parent:Cexists) {
			super(data, parent, 'argument')
		}
	}
}
export namespace Cnode__client_bindings {
	export class Dbindings extends AlanDictionary<{ node:Cbindings__node, init:Tbindings__node},Cnode__client_bindings> {
		protected initialize(parent:Cnode__client_bindings, key:string, entry_init:Tbindings__node) { return new Cbindings__node(key, entry_init, parent); }
		protected finalize = finalize_bindings__node
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bindings'`; }
		constructor(data:Tnode__client_bindings['bindings'], parent:Cnode__client_bindings) {
			super(data, parent);
		}
	}
	export class Dhas_widget extends StateGroup<
		{ name: 'no', node:Cno__has_widget, init:Tno__has_widget}|
		{ name: 'yes', node:Cyes__has_widget, init:Tyes__has_widget}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_widget, parent:Cnode__client_bindings) => new Cno__has_widget(init, parent);
				case 'yes': return (init:Tyes__has_widget, parent:Cnode__client_bindings) => new Cyes__has_widget(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_widget;
				case 'yes': return finalize_yes__has_widget;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__client_bindings['has widget'], parent:Cnode__client_bindings) {
			super(data, parent, 'has widget');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has widget'`; }
	}
	export class Dinstructions extends AlanDictionary<{ node:Cinstructions, init:Tinstructions},Cnode__client_bindings> {
		protected initialize(parent:Cnode__client_bindings, key:string, entry_init:Tinstructions) { return new Cinstructions(key, entry_init, parent); }
		protected finalize = finalize_instructions
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instructions'`; }
		constructor(data:Tnode__client_bindings['instructions'], parent:Cnode__client_bindings) {
			super(data, parent);
		}
	}
	export class Dproperties extends AlanDictionary<{ node:Cproperties, init:Tproperties},Cnode__client_bindings> {
		protected initialize(parent:Cnode__client_bindings, key:string, entry_init:Tproperties) { return new Cproperties(key, entry_init, parent); }
		protected finalize = finalize_properties
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode__client_bindings['properties'], parent:Cnode__client_bindings) {
			super(data, parent);
		}
	}
	export class Dsortable extends StateGroup<
		{ name: 'no', node:Cno__sortable, init:Tno__sortable}|
		{ name: 'yes', node:Cyes__sortable, init:Tyes__sortable}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__sortable, parent:Cnode__client_bindings) => new Cno__sortable(init, parent);
				case 'yes': return (init:Tyes__sortable, parent:Cnode__client_bindings) => new Cyes__sortable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__sortable;
				case 'yes': return finalize_yes__sortable;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__client_bindings['sortable'], parent:Cnode__client_bindings) {
			super(data, parent, 'sortable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sortable'`; }
	}
}
export namespace Cbindings__node {
	export class Dbinding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			binding: () => client_bindings.Cyes__constrained_on_containing_binding
		}

		constructor(data:string, $this:Cbindings__node) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				binding: cache((detach:boolean) => {
					const client_bindings__node__bindings__binding_nval = $this.properties.binding.ref;
					return resolve($this.properties.binding.ref)
						.then(context => client_bindings__node__bindings__binding_nval)
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'yes') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as client_bindings.Cyes__constrained_on_containing_binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
	export class Dswitchable_in_configuration_context_path extends StateGroup<
		{ name: 'no', node:Cno__switchable_in_configuration_context_path, init:Tno__switchable_in_configuration_context_path}|
		{ name: 'yes', node:Cyes__switchable_in_configuration_context_path, init:Tyes__switchable_in_configuration_context_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__switchable_in_configuration_context_path, parent:Cbindings__node) => new Cno__switchable_in_configuration_context_path(init, parent);
				case 'yes': return (init:Tyes__switchable_in_configuration_context_path, parent:Cbindings__node) => new Cyes__switchable_in_configuration_context_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__switchable_in_configuration_context_path;
				case 'yes': return finalize_yes__switchable_in_configuration_context_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbindings__node['switchable in configuration context path'], parent:Cbindings__node) {
			super(data, parent, 'switchable in configuration context path');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'switchable in configuration context path'`; }
	}
}
export namespace Cinstructions {
	export class Dhas_arguments extends StateGroup<
		{ name: 'no', node:Cno__has_arguments, init:Tno__has_arguments}|
		{ name: 'yes', node:Cyes__has_arguments, init:Tyes__has_arguments}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_arguments, parent:Cinstructions) => new Cno__has_arguments(init, parent);
				case 'yes': return (init:Tyes__has_arguments, parent:Cinstructions) => new Cyes__has_arguments(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_arguments;
				case 'yes': return finalize_yes__has_arguments;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstructions['has arguments'], parent:Cinstructions) {
			super(data, parent, 'has arguments');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has arguments'`; }
	}
}
export namespace Cyes__has_arguments {
	export class Dfirst_argument extends Cinstruction_argument {
		constructor(data:Tyes__has_arguments['first argument'], parent:Cyes__has_arguments) {
			super(data, parent, 'first argument')
		}
	}
}
export namespace Cproperties {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties, init:Tcollection__type__properties}|
		{ name: 'number', node:Cnumber__type__properties, init:Tnumber__type__properties}|
		{ name: 'reference', node:Creference, init:Treference}|
		{ name: 'state group', node:Cstate_group__type__properties, init:Tstate_group__type__properties}|
		{ name: 'text', node:Ctext__type__properties, init:Ttext__type__properties}> {
		protected initializer(state: 'collection'|'number'|'reference'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties, parent:Cproperties) => new Ccollection__type__properties(init, parent);
				case 'number': return (init:Tnumber__type__properties, parent:Cproperties) => new Cnumber__type__properties(init, parent);
				case 'reference': return (init:Treference, parent:Cproperties) => new Creference(init, parent);
				case 'state group': return (init:Tstate_group__type__properties, parent:Cproperties) => new Cstate_group__type__properties(init, parent);
				case 'text': return (init:Ttext__type__properties, parent:Cproperties) => new Ctext__type__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'number'|'reference'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties;
				case 'number': return finalize_number__type__properties;
				case 'reference': return finalize_reference;
				case 'state group': return finalize_state_group__type__properties;
				case 'text': return finalize_text__type__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties['type'], parent:Cproperties) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__properties {
	export class Dnode extends Cnode__client_bindings {
		constructor(data:Tcollection__type__properties['node'], parent:Ccollection__type__properties) {
			super(data, parent, {
				binding_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.binding_type()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!)
			}, 'node')
		}
	}
}
export namespace Creference {
	export class Dcan_navigate_binding extends StateGroup<
		{ name: 'no', node:Cno__can_navigate_binding, init:Tno__can_navigate_binding}|
		{ name: 'yes', node:Cyes__can_navigate_binding, init:Tyes__can_navigate_binding}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_navigate_binding, parent:Creference) => new Cno__can_navigate_binding(init, parent);
				case 'yes': return (init:Tyes__can_navigate_binding, parent:Creference) => new Cyes__can_navigate_binding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__can_navigate_binding;
				case 'yes': return finalize_yes__can_navigate_binding;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference['can navigate binding'], parent:Creference) {
			super(data, parent, 'can navigate binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can navigate binding'`; }
	}
}
export namespace Cyes__can_navigate_binding {
	export class Dbinding_type_path extends Cbinding_type_path {
		constructor(data:Tyes__can_navigate_binding['binding type path'], parent:Cyes__can_navigate_binding) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context?.state.node.output.node()).result!)
			}, 'binding type path')
		}
	}
	export class Dcontext extends StateGroup<
		{ name: 'root', node:Croot, init:Troot}|
		{ name: 'this', node:Cthis, init:Tthis}> {
		protected initializer(state: 'root'|'this') {
			switch (state) {
				case 'root': return (init:Troot, parent:Cyes__can_navigate_binding) => new Croot(init, parent);
				case 'this': return (init:Tthis, parent:Cyes__can_navigate_binding) => new Cthis(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'root'|'this') {
			switch (state) {
				case 'root': return finalize_root;
				case 'this': return finalize_this;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__can_navigate_binding['context'], parent:Cyes__can_navigate_binding) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Croot {
	export class Dbinding extends Reference<client_bindings.Cbindings__client_bindings,string> {

		constructor(data:string, $this:Croot) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
}
export namespace Cstate_group__type__properties {
	export class Dstates extends AlanDictionary<{ node:Cstates, init:Tstates},Cstate_group__type__properties> {
		protected initialize(parent:Cstate_group__type__properties, key:string, entry_init:Tstates) { return new Cstates(key, entry_init, parent); }
		protected finalize = finalize_states
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__properties['states'], parent:Cstate_group__type__properties) {
			super(data, parent);
		}
	}
}
export namespace Cstates {
	export class Dnode extends Cnode__client_bindings {
		constructor(data:Tstates['node'], parent:Cstates) {
			super(data, parent, {
				binding_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.binding_type()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!)
			}, 'node')
		}
	}
}
export namespace Cclient_bindings {
	export class Dbindings extends AlanDictionary<{ node:Cbindings__client_bindings, init:Tbindings__client_bindings},Cclient_bindings> {
		protected initialize(parent:Cclient_bindings, key:string, entry_init:Tbindings__client_bindings) { return new Cbindings__client_bindings(key, entry_init, parent); }
		protected finalize = finalize_bindings__client_bindings
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bindings'`; }
		constructor(data:Tclient_bindings['bindings'], parent:Cclient_bindings) {
			super(data, parent);
		}
	}
	export class Dformatters extends AlanDictionary<{ node:Cformatters, init:Tformatters},Cclient_bindings> {
		protected initialize(parent:Cclient_bindings, key:string, entry_init:Tformatters) { return new Cformatters(key, entry_init, parent); }
		protected finalize = finalize_formatters
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'formatters'`; }
		constructor(data:Tclient_bindings['formatters'], parent:Cclient_bindings) {
			super(data, parent);
		}
	}
	export class Dinitial_view_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {

		constructor(data:string, $this:Cclient_bindings) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initial view binding'`; }
	}
	export class Dtransformers extends AlanDictionary<{ node:Ctransformers, init:Ttransformers},Cclient_bindings> {
		protected initialize(parent:Cclient_bindings, key:string, entry_init:Ttransformers) { return new Ctransformers(key, entry_init, parent); }
		protected finalize = finalize_transformers
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'transformers'`; }
		constructor(data:Tclient_bindings['transformers'], parent:Cclient_bindings) {
			super(data, parent);
		}
	}
}
export namespace Cbindings__client_bindings {
	export class Dbinding extends Cnode__client_bindings {
		constructor(data:Tbindings__client_bindings['binding'], parent:Cbindings__client_bindings) {
			super(data, parent, {
				binding_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.binding_type()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!)
			}, 'binding')
		}
	}
	export class Dconstrained_on_containing_binding extends StateGroup<
		{ name: 'no', node:Cno__constrained_on_containing_binding, init:Tno__constrained_on_containing_binding}|
		{ name: 'yes', node:Cyes__constrained_on_containing_binding, init:Tyes__constrained_on_containing_binding}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__constrained_on_containing_binding, parent:Cbindings__client_bindings) => new Cno__constrained_on_containing_binding(init, parent);
				case 'yes': return (init:Tyes__constrained_on_containing_binding, parent:Cbindings__client_bindings) => new Cyes__constrained_on_containing_binding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__constrained_on_containing_binding;
				case 'yes': return finalize_yes__constrained_on_containing_binding;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbindings__client_bindings['constrained on containing binding'], parent:Cbindings__client_bindings) {
			super(data, parent, 'constrained on containing binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'engine state', node:Cengine_state__type, init:Tengine_state__type}|
		{ name: 'node', node:Cnode__type__bindings, init:Tnode__type__bindings}|
		{ name: 'query', node:Cquery__type__bindings, init:Tquery__type__bindings}|
		{ name: 'report', node:Creport, init:Treport}|
		{ name: 'role', node:Crole__type, init:Trole__type}|
		{ name: 'user', node:Cuser__type, init:Tuser__type}|
		{ name: 'view', node:Cview__type__bindings, init:Tview__type__bindings}|
		{ name: 'window', node:Cwindow__type, init:Twindow__type}> {
		protected initializer(state: 'engine state'|'node'|'query'|'report'|'role'|'user'|'view'|'window') {
			switch (state) {
				case 'engine state': return (init:Tengine_state__type, parent:Cbindings__client_bindings) => new Cengine_state__type(init, parent);
				case 'node': return (init:Tnode__type__bindings, parent:Cbindings__client_bindings) => new Cnode__type__bindings(init, parent);
				case 'query': return (init:Tquery__type__bindings, parent:Cbindings__client_bindings) => new Cquery__type__bindings(init, parent);
				case 'report': return (init:Treport, parent:Cbindings__client_bindings) => new Creport(init, parent);
				case 'role': return (init:Trole__type, parent:Cbindings__client_bindings) => new Crole__type(init, parent);
				case 'user': return (init:Tuser__type, parent:Cbindings__client_bindings) => new Cuser__type(init, parent);
				case 'view': return (init:Tview__type__bindings, parent:Cbindings__client_bindings) => new Cview__type__bindings(init, parent);
				case 'window': return (init:Twindow__type, parent:Cbindings__client_bindings) => new Cwindow__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'engine state'|'node'|'query'|'report'|'role'|'user'|'view'|'window') {
			switch (state) {
				case 'engine state': return finalize_engine_state__type;
				case 'node': return finalize_node__type__bindings;
				case 'query': return finalize_query__type__bindings;
				case 'report': return finalize_report;
				case 'role': return finalize_role__type;
				case 'user': return finalize_user__type;
				case 'view': return finalize_view__type__bindings;
				case 'window': return finalize_window__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbindings__client_bindings['type'], parent:Cbindings__client_bindings) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnode__type__bindings {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__node, init:Tcollection__type__node}|
		{ name: 'collection entry', node:Ccollection_entry__type, init:Tcollection_entry__type}|
		{ name: 'entity', node:Centity__type, init:Tentity__type}|
		{ name: 'file', node:Cfile__type__node, init:Tfile__type__node}|
		{ name: 'group', node:Cgroup__type, init:Tgroup__type}|
		{ name: 'join', node:Cjoin__type, init:Tjoin__type}|
		{ name: 'joined entry', node:Cjoined_entry__type, init:Tjoined_entry__type}|
		{ name: 'node', node:Cnode__type__node, init:Tnode__type__node}|
		{ name: 'number', node:Cnumber__type__node, init:Tnumber__type__node}|
		{ name: 'operation', node:Coperation__type, init:Toperation__type}|
		{ name: 'ordered graph', node:Cordered_graph__type, init:Tordered_graph__type}|
		{ name: 'query', node:Cquery__type__node, init:Tquery__type__node}|
		{ name: 'sort property', node:Csort_property__type, init:Tsort_property__type}|
		{ name: 'state', node:Cstate__type__node, init:Tstate__type__node}|
		{ name: 'state group', node:Cstate_group__type__node, init:Tstate_group__type__node}|
		{ name: 'text', node:Ctext__type__node, init:Ttext__type__node}|
		{ name: 'where rule', node:Cwhere_rule__type, init:Twhere_rule__type}> {
		protected initializer(state: 'collection'|'collection entry'|'entity'|'file'|'group'|'join'|'joined entry'|'node'|'number'|'operation'|'ordered graph'|'query'|'sort property'|'state'|'state group'|'text'|'where rule') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__node, parent:Cnode__type__bindings) => new Ccollection__type__node(init, parent);
				case 'collection entry': return (init:Tcollection_entry__type, parent:Cnode__type__bindings) => new Ccollection_entry__type(init, parent);
				case 'entity': return (init:Tentity__type, parent:Cnode__type__bindings) => new Centity__type(init, parent);
				case 'file': return (init:Tfile__type__node, parent:Cnode__type__bindings) => new Cfile__type__node(init, parent);
				case 'group': return (init:Tgroup__type, parent:Cnode__type__bindings) => new Cgroup__type(init, parent);
				case 'join': return (init:Tjoin__type, parent:Cnode__type__bindings) => new Cjoin__type(init, parent);
				case 'joined entry': return (init:Tjoined_entry__type, parent:Cnode__type__bindings) => new Cjoined_entry__type(init, parent);
				case 'node': return (init:Tnode__type__node, parent:Cnode__type__bindings) => new Cnode__type__node(init, parent);
				case 'number': return (init:Tnumber__type__node, parent:Cnode__type__bindings) => new Cnumber__type__node(init, parent);
				case 'operation': return (init:Toperation__type, parent:Cnode__type__bindings) => new Coperation__type(init, parent);
				case 'ordered graph': return (init:Tordered_graph__type, parent:Cnode__type__bindings) => new Cordered_graph__type(init, parent);
				case 'query': return (init:Tquery__type__node, parent:Cnode__type__bindings) => new Cquery__type__node(init, parent);
				case 'sort property': return (init:Tsort_property__type, parent:Cnode__type__bindings) => new Csort_property__type(init, parent);
				case 'state': return (init:Tstate__type__node, parent:Cnode__type__bindings) => new Cstate__type__node(init, parent);
				case 'state group': return (init:Tstate_group__type__node, parent:Cnode__type__bindings) => new Cstate_group__type__node(init, parent);
				case 'text': return (init:Ttext__type__node, parent:Cnode__type__bindings) => new Ctext__type__node(init, parent);
				case 'where rule': return (init:Twhere_rule__type, parent:Cnode__type__bindings) => new Cwhere_rule__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'collection entry'|'entity'|'file'|'group'|'join'|'joined entry'|'node'|'number'|'operation'|'ordered graph'|'query'|'sort property'|'state'|'state group'|'text'|'where rule') {
			switch (state) {
				case 'collection': return finalize_collection__type__node;
				case 'collection entry': return finalize_collection_entry__type;
				case 'entity': return finalize_entity__type;
				case 'file': return finalize_file__type__node;
				case 'group': return finalize_group__type;
				case 'join': return finalize_join__type;
				case 'joined entry': return finalize_joined_entry__type;
				case 'node': return finalize_node__type__node;
				case 'number': return finalize_number__type__node;
				case 'operation': return finalize_operation__type;
				case 'ordered graph': return finalize_ordered_graph__type;
				case 'query': return finalize_query__type__node;
				case 'sort property': return finalize_sort_property__type;
				case 'state': return finalize_state__type__node;
				case 'state group': return finalize_state_group__type__node;
				case 'text': return finalize_text__type__node;
				case 'where rule': return finalize_where_rule__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__type__bindings['type'], parent:Cnode__type__bindings) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cquery__type__bindings {
	export class Dtype extends StateGroup<
		{ name: 'entry', node:Centry__type__query, init:Tentry__type__query}|
		{ name: 'file', node:Cfile__type__query, init:Tfile__type__query}|
		{ name: 'number', node:Cnumber__type__query, init:Tnumber__type__query}|
		{ name: 'stategroup', node:Cstategroup, init:Tstategroup}|
		{ name: 'text', node:Ctext__type__query, init:Ttext__type__query}> {
		protected initializer(state: 'entry'|'file'|'number'|'stategroup'|'text') {
			switch (state) {
				case 'entry': return (init:Tentry__type__query, parent:Cquery__type__bindings) => new Centry__type__query(init, parent);
				case 'file': return (init:Tfile__type__query, parent:Cquery__type__bindings) => new Cfile__type__query(init, parent);
				case 'number': return (init:Tnumber__type__query, parent:Cquery__type__bindings) => new Cnumber__type__query(init, parent);
				case 'stategroup': return (init:Tstategroup, parent:Cquery__type__bindings) => new Cstategroup(init, parent);
				case 'text': return (init:Ttext__type__query, parent:Cquery__type__bindings) => new Ctext__type__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'file'|'number'|'stategroup'|'text') {
			switch (state) {
				case 'entry': return finalize_entry__type__query;
				case 'file': return finalize_file__type__query;
				case 'number': return finalize_number__type__query;
				case 'stategroup': return finalize_stategroup;
				case 'text': return finalize_text__type__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__type__bindings['type'], parent:Cquery__type__bindings) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__query {
	export class Dtype extends StateGroup<
		{ name: 'column', node:Ccolumn__type__number, init:Tcolumn__type__number}|
		{ name: 'entry', node:Centry__type__number, init:Tentry__type__number}> {
		protected initializer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return (init:Tcolumn__type__number, parent:Cnumber__type__query) => new Ccolumn__type__number(init, parent);
				case 'entry': return (init:Tentry__type__number, parent:Cnumber__type__query) => new Centry__type__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return finalize_column__type__number;
				case 'entry': return finalize_entry__type__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__query['type'], parent:Cnumber__type__query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstategroup {
	export class Dtype extends StateGroup<
		{ name: 'column', node:Ccolumn__type__stategroup, init:Tcolumn__type__stategroup}|
		{ name: 'entry', node:Centry__type__stategroup, init:Tentry__type__stategroup}> {
		protected initializer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return (init:Tcolumn__type__stategroup, parent:Cstategroup) => new Ccolumn__type__stategroup(init, parent);
				case 'entry': return (init:Tentry__type__stategroup, parent:Cstategroup) => new Centry__type__stategroup(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return finalize_column__type__stategroup;
				case 'entry': return finalize_entry__type__stategroup;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstategroup['type'], parent:Cstategroup) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ctext__type__query {
	export class Dtype extends StateGroup<
		{ name: 'column', node:Ccolumn__type__text, init:Tcolumn__type__text}|
		{ name: 'entry', node:Centry__type__text, init:Tentry__type__text}> {
		protected initializer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return (init:Tcolumn__type__text, parent:Ctext__type__query) => new Ccolumn__type__text(init, parent);
				case 'entry': return (init:Tentry__type__text, parent:Ctext__type__query) => new Centry__type__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'column'|'entry') {
			switch (state) {
				case 'column': return finalize_column__type__text;
				case 'entry': return finalize_entry__type__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__query['type'], parent:Ctext__type__query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Creport {
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode__type__report, init:Tnode__type__report}|
		{ name: 'number', node:Cnumber__type__report, init:Tnumber__type__report}|
		{ name: 'text', node:Ctext__type__report, init:Ttext__type__report}> {
		protected initializer(state: 'node'|'number'|'text') {
			switch (state) {
				case 'node': return (init:Tnode__type__report, parent:Creport) => new Cnode__type__report(init, parent);
				case 'number': return (init:Tnumber__type__report, parent:Creport) => new Cnumber__type__report(init, parent);
				case 'text': return (init:Ttext__type__report, parent:Creport) => new Ctext__type__report(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'number'|'text') {
			switch (state) {
				case 'node': return finalize_node__type__report;
				case 'number': return finalize_number__type__report;
				case 'text': return finalize_text__type__report;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treport['type'], parent:Creport) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cformatters {
	export class Dfrom extends StateGroup<
		{ name: 'number', node:Cnumber__from__formatters, init:Tnumber__from__formatters}|
		{ name: 'text', node:Ctext__from__formatters, init:Ttext__from__formatters}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__from__formatters, parent:Cformatters) => new Cnumber__from__formatters(init, parent);
				case 'text': return (init:Ttext__from__formatters, parent:Cformatters) => new Ctext__from__formatters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'text') {
			switch (state) {
				case 'number': return finalize_number__from__formatters;
				case 'text': return finalize_text__from__formatters;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tformatters['from'], parent:Cformatters) {
			super(data, parent, 'from');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'from'`; }
	}
}
export namespace Ctransformers {
	export class Dfrom extends StateGroup<
		{ name: 'number', node:Cnumber__from__transformers, init:Tnumber__from__transformers}|
		{ name: 'text', node:Ctext__from__transformers, init:Ttext__from__transformers}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__from__transformers, parent:Ctransformers) => new Cnumber__from__transformers(init, parent);
				case 'text': return (init:Ttext__from__transformers, parent:Ctransformers) => new Ctext__from__transformers(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'text') {
			switch (state) {
				case 'number': return finalize_number__from__transformers;
				case 'text': return finalize_text__from__transformers;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttransformers['from'], parent:Ctransformers) {
			super(data, parent, 'from');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'from'`; }
	}
}
function finalize_binding_context(obj:Cbinding_context, detach:boolean = false) {
}
function finalize_binding_type(obj:Cbinding_type, detach:boolean = false) {
}
function finalize_no__has_next(obj:Cno__has_next, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result)(detach) !== undefined || detach);
}
function finalize_yes__has_next(obj:Cyes__has_next, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result)(detach) !== undefined || detach);
	finalize_binding_type_path(obj.properties.tail, detach);
}
function finalize_binding(obj:Cbinding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
}
function finalize_collection__type__property(obj:Ccollection__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ccollection__type__properties>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__type__property(obj:Cstate__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstates>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstate_group__type__properties>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_property(obj:Cproperty, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_binding_type_path(obj:Cbinding_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result)(detach) !== undefined || detach);
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next(obj.properties.has_next.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'binding': finalize_binding(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entry_key(obj:Centry_key, detach:boolean = false) {
	finalize_key(obj.definitions.key, detach);
}
function finalize_exists(obj:Cexists, detach:boolean = false) {
	finalize_instruction_argument(obj.properties.argument, detach);
}
function finalize_no__next_argument(obj:Cno__next_argument, detach:boolean = false) {
}
function finalize_file__type__instruction_argument(obj:Cfile__type__instruction_argument, detach:boolean = false) {
}
function finalize_number__type__instruction_argument(obj:Cnumber__type__instruction_argument, detach:boolean = false) {
}
function finalize_text__type__instruction_argument(obj:Ctext__type__instruction_argument, detach:boolean = false) {
}
function finalize_view__type__instruction_argument(obj:Cview__type__instruction_argument, detach:boolean = false) {
}
function finalize_instruction_argument(obj:Cinstruction_argument, detach:boolean = false) {
	switch (obj.properties.next_argument.state.name) {
		case 'exists': finalize_exists(obj.properties.next_argument.state.node, detach); break;
		case 'no': finalize_no__next_argument(obj.properties.next_argument.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'file': finalize_file__type__instruction_argument(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__instruction_argument(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__instruction_argument(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__instruction_argument(obj.properties.type.state.node, detach); break;
	}
}
function finalize_key(obj:Ckey, detach:boolean = false) {
}
function finalize_no__switchable_in_configuration_context_path(obj:Cno__switchable_in_configuration_context_path, detach:boolean = false) {
}
function finalize_yes__switchable_in_configuration_context_path(obj:Cyes__switchable_in_configuration_context_path, detach:boolean = false) {
}
function finalize_bindings__node(obj:Cbindings__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__constrained_on_containing_binding>obj.properties.binding.inferences.binding)(detach) !== undefined || detach);
	switch (obj.properties.switchable_in_configuration_context_path.state.name) {
		case 'no': finalize_no__switchable_in_configuration_context_path(obj.properties.switchable_in_configuration_context_path.state.node, detach); break;
		case 'yes': finalize_yes__switchable_in_configuration_context_path(obj.properties.switchable_in_configuration_context_path.state.node, detach); break;
	}
}
function finalize_no__has_widget(obj:Cno__has_widget, detach:boolean = false) {
}
function finalize_yes__has_widget(obj:Cyes__has_widget, detach:boolean = false) {
}
function finalize_no__has_arguments(obj:Cno__has_arguments, detach:boolean = false) {
}
function finalize_yes__has_arguments(obj:Cyes__has_arguments, detach:boolean = false) {
	finalize_instruction_argument(obj.properties.first_argument, detach);
}
function finalize_instructions(obj:Cinstructions, detach:boolean = false) {
	switch (obj.properties.has_arguments.state.name) {
		case 'no': finalize_no__has_arguments(obj.properties.has_arguments.state.node, detach); break;
		case 'yes': finalize_yes__has_arguments(obj.properties.has_arguments.state.node, detach); break;
	}
}
function finalize_collection__type__properties(obj:Ccollection__type__properties, detach:boolean = false) {
	finalize_entry_key(obj.definitions.entry_key, detach);
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_node__client_bindings(obj.properties.node, detach);
}
function finalize_number__type__properties(obj:Cnumber__type__properties, detach:boolean = false) {
}
function finalize_no__can_navigate_binding(obj:Cno__can_navigate_binding, detach:boolean = false) {
}
function finalize_root(obj:Croot, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
}
function finalize_this(obj:Cthis, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
}
function finalize_yes__can_navigate_binding(obj:Cyes__can_navigate_binding, detach:boolean = false) {
	finalize_binding_type_path(obj.properties.binding_type_path, detach);
	switch (obj.properties.context.state.name) {
		case 'root': finalize_root(obj.properties.context.state.node, detach); break;
		case 'this': finalize_this(obj.properties.context.state.node, detach); break;
	}
}
function finalize_reference(obj:Creference, detach:boolean = false) {
	switch (obj.properties.can_navigate_binding.state.name) {
		case 'no': finalize_no__can_navigate_binding(obj.properties.can_navigate_binding.state.node, detach); break;
		case 'yes': finalize_yes__can_navigate_binding(obj.properties.can_navigate_binding.state.node, detach); break;
	}
}
function finalize_states(obj:Cstates, detach:boolean = false) {
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_node__client_bindings(obj.properties.node, detach);
}
function finalize_state_group__type__properties(obj:Cstate_group__type__properties, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.states) {
		finalize_states(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_text__type__properties(obj:Ctext__type__properties, detach:boolean = false) {
}
function finalize_properties(obj:Cproperties, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__sortable(obj:Cno__sortable, detach:boolean = false) {
}
function finalize_yes__sortable(obj:Cyes__sortable, detach:boolean = false) {
}
function finalize_node__client_bindings(obj:Cnode__client_bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.input.binding_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode_location>obj.output.location)(detach) !== undefined || detach);
	finalize_binding_context(obj.definitions.binding_context, detach);
	for (const [_key, entry] of obj.properties.bindings) {
		finalize_bindings__node(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.has_widget.state.name) {
		case 'no': finalize_no__has_widget(obj.properties.has_widget.state.node, detach); break;
		case 'yes': finalize_yes__has_widget(obj.properties.has_widget.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.instructions) {
		finalize_instructions(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.sortable.state.name) {
		case 'no': finalize_no__sortable(obj.properties.sortable.state.node, detach); break;
		case 'yes': finalize_yes__sortable(obj.properties.sortable.state.node, detach); break;
	}
}
function finalize_node_binding_type(obj:Cnode_binding_type, detach:boolean = false) {
	finalize_binding_type(obj.definitions.binding_type, detach);
}
function finalize_node_location(obj:Cnode_location, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.input.root)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ckey>obj.output.key)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.root)(detach) !== undefined || detach);
}
function finalize_query_binding_type(obj:Cquery_binding_type, detach:boolean = false) {
	finalize_binding_type(obj.definitions.binding_type, detach);
}
function finalize_report_binding_type(obj:Creport_binding_type, detach:boolean = false) {
	finalize_binding_type(obj.definitions.binding_type, detach);
}
function finalize_no__constrained_on_containing_binding(obj:Cno__constrained_on_containing_binding, detach:boolean = false) {
}
function finalize_yes__constrained_on_containing_binding(obj:Cyes__constrained_on_containing_binding, detach:boolean = false) {
}
function finalize_engine_state__type(obj:Cengine_state__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_collection__type__node(obj:Ccollection__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_collection_entry__type(obj:Ccollection_entry__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	finalize_entry_key(obj.definitions.entry_key, detach);
}
function finalize_entity__type(obj:Centity__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_file__type__node(obj:Cfile__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_group__type(obj:Cgroup__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_join__type(obj:Cjoin__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_joined_entry__type(obj:Cjoined_entry__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	finalize_entry_key(obj.definitions.entry_key, detach);
}
function finalize_node__type__node(obj:Cnode__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_number__type__node(obj:Cnumber__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_operation__type(obj:Coperation__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_ordered_graph__type(obj:Cordered_graph__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_query__type__node(obj:Cquery__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_sort_property__type(obj:Csort_property__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_state__type__node(obj:Cstate__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_state_group__type__node(obj:Cstate_group__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_text__type__node(obj:Ctext__type__node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_where_rule__type(obj:Cwhere_rule__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_node__type__bindings(obj:Cnode__type__bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__node(obj.properties.type.state.node, detach); break;
		case 'collection entry': finalize_collection_entry__type(obj.properties.type.state.node, detach); break;
		case 'entity': finalize_entity__type(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__node(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type(obj.properties.type.state.node, detach); break;
		case 'join': finalize_join__type(obj.properties.type.state.node, detach); break;
		case 'joined entry': finalize_joined_entry__type(obj.properties.type.state.node, detach); break;
		case 'node': finalize_node__type__node(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__node(obj.properties.type.state.node, detach); break;
		case 'operation': finalize_operation__type(obj.properties.type.state.node, detach); break;
		case 'ordered graph': finalize_ordered_graph__type(obj.properties.type.state.node, detach); break;
		case 'query': finalize_query__type__node(obj.properties.type.state.node, detach); break;
		case 'sort property': finalize_sort_property__type(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__node(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__node(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__node(obj.properties.type.state.node, detach); break;
		case 'where rule': finalize_where_rule__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entry__type__query(obj:Centry__type__query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	finalize_entry_key(obj.definitions.entry_key, detach);
}
function finalize_file__type__query(obj:Cfile__type__query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_column__type__number(obj:Ccolumn__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_entry__type__number(obj:Centry__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_number__type__query(obj:Cnumber__type__query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'column': finalize_column__type__number(obj.properties.type.state.node, detach); break;
		case 'entry': finalize_entry__type__number(obj.properties.type.state.node, detach); break;
	}
}
function finalize_column__type__stategroup(obj:Ccolumn__type__stategroup, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_entry__type__stategroup(obj:Centry__type__stategroup, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_stategroup(obj:Cstategroup, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'column': finalize_column__type__stategroup(obj.properties.type.state.node, detach); break;
		case 'entry': finalize_entry__type__stategroup(obj.properties.type.state.node, detach); break;
	}
}
function finalize_column__type__text(obj:Ccolumn__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_entry__type__text(obj:Centry__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_text__type__query(obj:Ctext__type__query, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cquery_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'column': finalize_column__type__text(obj.properties.type.state.node, detach); break;
		case 'entry': finalize_entry__type__text(obj.properties.type.state.node, detach); break;
	}
}
function finalize_query__type__bindings(obj:Cquery__type__bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'entry': finalize_entry__type__query(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__query(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__query(obj.properties.type.state.node, detach); break;
		case 'stategroup': finalize_stategroup(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node__type__report(obj:Cnode__type__report, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Creport_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_number__type__report(obj:Cnumber__type__report, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Creport_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_text__type__report(obj:Ctext__type__report, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Creport_binding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_report(obj:Creport, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node__type__report(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__report(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__report(obj.properties.type.state.node, detach); break;
	}
}
function finalize_role__type(obj:Crole__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_user__type(obj:Cuser__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_view__type__bindings(obj:Cview__type__bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_window__type(obj:Cwindow__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_type>obj.output.binding_type)(detach) !== undefined || detach);
}
function finalize_bindings__client_bindings(obj:Cbindings__client_bindings, detach:boolean = false) {
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_node__client_bindings(obj.properties.binding, detach);
	switch (obj.properties.constrained_on_containing_binding.state.name) {
		case 'no': finalize_no__constrained_on_containing_binding(obj.properties.constrained_on_containing_binding.state.node, detach); break;
		case 'yes': finalize_yes__constrained_on_containing_binding(obj.properties.constrained_on_containing_binding.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'engine state': finalize_engine_state__type(obj.properties.type.state.node, detach); break;
		case 'node': finalize_node__type__bindings(obj.properties.type.state.node, detach); break;
		case 'query': finalize_query__type__bindings(obj.properties.type.state.node, detach); break;
		case 'report': finalize_report(obj.properties.type.state.node, detach); break;
		case 'role': finalize_role__type(obj.properties.type.state.node, detach); break;
		case 'user': finalize_user__type(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__bindings(obj.properties.type.state.node, detach); break;
		case 'window': finalize_window__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__from__formatters(obj:Cnumber__from__formatters, detach:boolean = false) {
}
function finalize_text__from__formatters(obj:Ctext__from__formatters, detach:boolean = false) {
}
function finalize_formatters(obj:Cformatters, detach:boolean = false) {
	switch (obj.properties.from.state.name) {
		case 'number': finalize_number__from__formatters(obj.properties.from.state.node, detach); break;
		case 'text': finalize_text__from__formatters(obj.properties.from.state.node, detach); break;
	}
}
function finalize_number__from__transformers(obj:Cnumber__from__transformers, detach:boolean = false) {
}
function finalize_text__from__transformers(obj:Ctext__from__transformers, detach:boolean = false) {
}
function finalize_transformers(obj:Ctransformers, detach:boolean = false) {
	switch (obj.properties.from.state.name) {
		case 'number': finalize_number__from__transformers(obj.properties.from.state.node, detach); break;
		case 'text': finalize_text__from__transformers(obj.properties.from.state.node, detach); break;
	}
}
function finalize_client_bindings(obj:Cclient_bindings, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.bindings) {
		finalize_bindings__client_bindings(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.formatters) {
		finalize_formatters(entry, detach);
	}
	if (!detach) {
	}
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.initial_view_binding as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.transformers) {
		finalize_transformers(entry, detach);
	}
	if (!detach) {
	}
}

export namespace Cclient_bindings {
	export function create(init:Tclient_bindings, symbols_file?: SymbolsFile):Cclient_bindings {
		const instance = new Cclient_bindings(init, symbols_file);
		finalize_client_bindings(instance);
		return instance;
	};
}
